import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import Nav from "../../components/home-v2/Nav";
import FloatingIcons, {
  video1url,
} from "../../components/home-v2/FloatingIcons";
import ScrollingLogos from "../../components/home-v2/ScrollingLogos";
import Tag from "../../components/home-v2/Tag";
import Card from "../../components/home-v2/Card";
import WorldSection from "../../components/home-v2/WorldSection";
import VerticalCard from "../../components/home-v2/VerticalCard";
import Footer from "../../components/home-v2/Footer";
import "./LandingPage.css";
import "../typography/Font.css";
import { FileUpIcon, SparklesIcon, WrenchIcon } from "lucide-react";
import LoginButton from "@/components/home-v2/LoginButton";

import ScrollToTop from "@/components/home-v2/ScrollToTop";

function LandingPage() {
  // Use the imported video variable for the initial state
  const [selectedMedia, setSelectedMedia] = useState(video1url);
  const [videoClass, setVideoClass] = useState("yellow-video");
  const [selectedTitle, setSelectedTitle] = useState(
    "Generate quality flashcards in seconds."
  );
  const [selectedDescription, setSelectedDescription] = useState(
    "Lorem ipsum dolor sit amet consectetur. Aliquet id bibendum at nulla."
  );
  const [darkNav, setDarkNav] = useState(false);
  const worldSectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (worldSectionRef.current) {
        const { top, bottom } = worldSectionRef.current.getBoundingClientRect();
        setDarkNav(top < -150 && bottom > 100);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleContentChange = (
    mediaPath: string, // Keep receiving the original path string
    title: string,
    description: string,
    videoClass?: string
  ) => {
    // Use the map to get the correct imported video variable
    setSelectedMedia(mediaPath || video1url); // Fallback to video1 if path not found
    setSelectedTitle(title);
    setSelectedDescription(description);
    setVideoClass(videoClass ?? "yellow-video");
  };

  return (
    <div className="home-v2">
      <Nav darkMode={darkNav} />
      <ScrollToTop />

      <div className="header">
        <motion.h1
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut", delay: 0.1 }}
          dangerouslySetInnerHTML={{ __html: selectedTitle }}
        />

        <motion.h4
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut", delay: 0.3 }}
        >
          {selectedDescription}
        </motion.h4>
      </div>

      <motion.div
        className="index"
        initial={{ opacity: 0, scale: 1, y: 30 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut", delay: 0.5 }}
      >
        <LoginButton variant="primary" size="xl">
          <FileUpIcon className="w-5 h-5 flex-none" />
          <span>Upload your Document</span>
        </LoginButton>
      </motion.div>

      <motion.div
        className="floating-icons"
        initial={{ opacity: 0, scale: 0.8, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut", delay: 0.5 }}
      >
        <FloatingIcons onContentChange={handleContentChange} />
      </motion.div>
      <div className="image-container">
        <video
          className={videoClass}
          src={selectedMedia}
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          disablePictureInPicture
          disableRemotePlayback
        />
      </div>

      <motion.div
        className="center"
        initial={{ opacity: 0, y: 70 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: "easeOut" }}
        viewport={{ once: true, amount: 0.5 }}
      >
        <ScrollingLogos />

        <div className="loved-by">
          <p>
            Loved by a community of more than{" "}
            <span className="bold">250k students</span>
            <br /> all around the globe
          </p>
        </div>
      </motion.div>

      <div className="center">
        <motion.div
          className="howitworks"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: -20 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          viewport={{ once: true, amount: 0.5 }}
        >
          <Tag variant="grey" size="m">
            <WrenchIcon className="w-icon-md h-icon-md flex-none" />
            How it works
          </Tag>
        </motion.div>
        <motion.div
          className="howitworks"
          initial={{ opacity: 0, y: 70 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          viewport={{ once: true, amount: 0.1 }}
        >
          <Card
            variant="sx"
            number="1"
            title="Upload your study materials"
            description="No matter what format you study, just upload it and start a new study journey."
            animationUrl="https://lottie.host/cc0f3ba0-e878-440c-84a0-16470fbe2e4c/AaOkq8tKln.lottie"
            buttonLink="/home/upload"
            buttonText="Learn More"
          />
          <Card
            variant="dx"
            number="2"
            title="Generate quality flashcards"
            description="Generate flashcards and image occlusions from a simple highlight."
            animationUrl="https://lottie.host/fd7cc11b-b1a3-42c4-ab2c-1376a9e0f79e/6lCXZgidFV.lottie"
            buttonLink="/home/flashcard"
            buttonText="Learn More"
          />
          <Card
            variant="sx"
            number="3"
            title="Study with Spaced Repetition"
            description="Our spaced repetition algorithm will manage your flashcard review maximizing memorization and minimizing effort!"
            animationUrl="https://lottie.host/ff54ca3f-8938-4443-91a0-d818dce10cbd/756FxlaIGA.lottie"
            buttonLink="/home/study"
            buttonText="Learn More"
          />
          <Card
            variant="dx"
            number="4"
            title="Test your knowledge with quizzes"
            description="Build confidence with hundreds of quizzes. We will give you explanations for each mistake."
            animationUrl="https://lottie.host/9242cae7-168c-4019-8422-f4bebf0490c1/oyWqXYhmUh.lottie"
            buttonLink="/home/quizzes"
            buttonText="Learn More"
          />
        </motion.div>
      </div>

      <div ref={worldSectionRef}>
        <WorldSection />
      </div>

      <div className="student">
        <h2>
          Made for <span className="yellow-text-text">every</span> student
        </h2>
        <p>
          We leverage the most advanced techniques for studying effectively and
          retaining memory for a long time.
        </p>
      </div>

      <div className="vertical-card-container">
        <VerticalCard
          lottieSrc="https://lottie.host/50e21e38-e30a-4934-9354-076be58b3cff/zfQUai5l17.lottie"
          title="Available in any language"
          description="Our AI tutor speaks any language! Our students come from 100+ different countries!"
          badges={[
            "🇪🇸 Español",
            "🇬🇧 English",
            "🇫🇮 Suomalainen",
            "🇮🇹 Italiano",
            "🇸🇦 العربية",
            "🇨🇳 中文",
          ]}
        />
        <VerticalCard
          lottieSrc="https://lottie.host/33e78e5b-234c-4cd9-be57-77ee48175c17/PBMigebB7I.lottie"
          title="Proven by science"
          description="Our spaced repetition algorithm maximizes memorization and minimizes effort!"
          buttonText="Learn more"
          buttonLink="/home/study"
        />
      </div>

      <div className="last-cta">
        <LoginButton variant="primary" size="xl">
          <SparklesIcon className="w-5 h-5 flex-none" />
          <span>Try it now for free</span>
        </LoginButton>
      </div>

      <Footer />
    </div>
  );
}

export default LandingPage;
